import { Suspense, lazy, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import {
  MODULES,
  type BuiltinFieldSetting,
  type MetadataResponse,
  type Module,
} from '@castinflow/shared';
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  DescriptionList,
  EmptyState,
  Field,
  Input,
  PageHeader,
  SettingsLayout,
  Skeleton,
  Tabs,
  cardSurface,
  cn,
  useToast,
} from '@castinflow/ui';
import { useSession, apiMutate } from '../lib/session';
import { useInvalidateMetadata, useMetadata } from '../lib/metadata';
import { useScope } from '../lib/scope';
import { usePageMeta } from '../shell/page-meta';
import { MODULE_LABEL, tr } from '../i18n/tr';
import { QueryErrorState } from './StatePages';

type Tab = 'settings' | 'fields' | 'feed' | 'lookups';

/** WP 2.9: kişi/şirket seçenek listeleri (ilişki tipi, kaynak, sektör, bölge) — tembel parça */
const LookupSettings = lazy(() => import('../framework/crm/LookupSettings'));
const LOOKUP_MODULES: readonly Module[] = ['contacts', 'companies'];
/** WP 2.10: ürün ayarları (7 sekme) — tembel parça; kategori/set/fiyat kategorisi yönetimi ilk pakete girmez */
const ProductSettings = lazy(() => import('../framework/products/ProductSettings'));
/** M4 WP 4.2: aktivite ayarları (4 sekme) — tembel parça */
const ActivitySettings = lazy(() => import('../framework/activities/ActivitySettings'));
const PRODUCT_TABS = [
  'general',
  'fields',
  'custom',
  'categories',
  'sets',
  'priceCategories',
  'print',
] as const;
type ProductTab = (typeof PRODUCT_TABS)[number];
/** M4 WP 4.4: destek talepleri ayarları (14 sekme, K-M4-22) — tembel parça */
const TicketSettings = lazy(() => import('../framework/support/TicketSettings'));
const isModule = (v: string | null): v is Module =>
  v !== null && (MODULES as readonly string[]).includes(v);
const isProductTab = (v: string | null): v is ProductTab =>
  v !== null && (PRODUCT_TABS as readonly string[]).includes(v);
type ModuleMeta = NonNullable<MetadataResponse['modules'][Module]>;
const DEFAULT_MODULE_SETTINGS: ModuleMeta['settings'] = {
  publishToFeed: true,
  referencePrefix: '',
  referenceNextNo: 1,
  requireOwner: true,
  allowOnlyForwardStage: false,
  extra: {},
};
function readString(fd: FormData, key: string): string {
  const v = fd.get(key);
  return typeof v === 'string' ? v : '';
}

/**
 * Ortak ayar sekmeleri (Plan §3.1 "SettingsTabsPage"): modül seçimi (sol bölüm menüsü) + Ayarlar · Bilgi giriş
 * alanları · Haber akışı. Yazma yetkisi olmayan kullanıcı değerleri görür, kaydedemez (sunucu da reddeder).
 */
export function SettingsTabsPage() {
  const me = useSession((s) => s.me);
  const scope = useScope();
  const metadata = useMetadata(scope);
  const invalidate = useInvalidateMetadata();
  const toast = useToast();
  // `?modul=products&sekme=categories` ile doğrudan bölüme gelinir (ör. ürün formundaki "kategori tanımla" bağlantısı)
  const [params] = useSearchParams();
  const [module, setModule] = useState<Module>(() => {
    const m = params.get('modul');
    return isModule(m) ? m : 'contacts';
  });
  const [tab, setTab] = useState<Tab>('settings');
  const initialProductTab = ((): ProductTab | undefined => {
    const s = params.get('sekme');
    return isProductTab(s) ? s : undefined;
  })();
  const canManage = me?.capabilities.includes('settings.manage') ?? false;
  usePageMeta(tr.settings.title, [{ label: tr.nav.administration }]);

  const saveSettings = useMutation({
    mutationFn: (settings: ModuleMeta['settings']) =>
      apiMutate(`/settings/modules/${module}`, 'PUT', settings),
    onSuccess: async () => {
      if (scope) await invalidate(scope);
      toast({ title: tr.settings.saved });
    },
    onError: () => {
      toast({ tone: 'danger', title: tr.settings.saveFailed });
    },
  });
  const saveFields = useMutation({
    mutationFn: (fields: BuiltinFieldSetting[]) =>
      apiMutate(`/settings/modules/${module}/fields`, 'PUT', {
        fields: fields.map(({ key, visible, required, sortOrder }) => ({
          key,
          visible,
          required,
          sortOrder,
        })),
      }),
    onSuccess: async () => {
      if (scope) await invalidate(scope);
      toast({ title: tr.settings.fieldsSaved });
    },
    onError: () => {
      toast({ tone: 'danger', title: tr.settings.saveFailed });
    },
  });

  if (!scope) return null;

  const header = (
    <PageHeader
      title={tr.settings.title}
      description={tr.settings.description}
      meta={
        metadata.data ? (
          <Badge>
            <span data-testid="metadata-version">
              {tr.settings.version(metadata.data.tenant.metadataVersion)}
            </span>
          </Badge>
        ) : null
      }
    />
  );

  let body;
  if (metadata.isPending) {
    body = (
      <div className="space-y-3" aria-busy="true">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-80" />
        <Skeleton className="h-4 w-72" />
        <span className="sr-only">Yükleniyor</span>
      </div>
    );
  } else if (metadata.isError) {
    body = <QueryErrorState error={metadata.error} onRetry={() => void metadata.refetch()} />;
  } else {
    const data = metadata.data;
    const mod = data.modules[module] ?? { settings: DEFAULT_MODULE_SETTINGS, fields: [] };
    const feed = data.feed.perModule[module];
    const moduleForm = (
      <form
        key={`${module}:${data.tenant.metadataVersion}`}
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          const fd = new FormData(e.currentTarget);
          saveSettings.mutate({
            ...mod.settings,
            publishToFeed: fd.get('publishToFeed') === 'on',
            referencePrefix: readString(fd, 'referencePrefix'),
            requireOwner: fd.get('requireOwner') === 'on',
            allowOnlyForwardStage: fd.get('allowOnlyForwardStage') === 'on',
          });
        }}
        data-testid="module-settings-form"
      >
        <fieldset className="space-y-3" disabled={!canManage}>
          <legend className="sr-only">{MODULE_LABEL[module]}</legend>
          <Checkbox
            name="publishToFeed"
            defaultChecked={mod.settings.publishToFeed}
            label={tr.settings.publishToFeed}
          />
          <Checkbox
            name="requireOwner"
            defaultChecked={mod.settings.requireOwner}
            label={tr.settings.requireOwner}
          />
          <Checkbox
            name="allowOnlyForwardStage"
            defaultChecked={mod.settings.allowOnlyForwardStage}
            label={tr.settings.forwardOnly}
          />
          <Field
            label={tr.settings.referencePrefix}
            description={tr.settings.referencePrefixHint}
            optional
            className="max-w-xs"
          >
            <Input
              name="referencePrefix"
              defaultValue={mod.settings.referencePrefix}
              data-testid="referencePrefix"
            />
          </Field>
        </fieldset>
        <Button
          type="submit"
          variant="primary"
          disabled={!canManage}
          loading={saveSettings.isPending}
          data-testid="save-settings"
        >
          {tr.settings.save}
        </Button>
      </form>
    );
    const fieldsTab = (noRequire?: ReadonlySet<string>) => (
      <FieldsTab
        key={`${module}:${data.tenant.metadataVersion}`}
        fields={mod.fields}
        noRequire={noRequire}
        disabled={!canManage || saveFields.isPending}
        busy={saveFields.isPending}
        onSave={(f) => {
          saveFields.mutate(f);
        }}
        onReset={() => {
          saveFields.mutate(
            mod.fields.map((f, i) => ({
              key: f.key,
              visible: true,
              required: f.locked,
              sortOrder: i,
            })),
          );
        }}
      />
    );
    // Aktivitede ortak ayar formunun yalnız "haber akışında yayınla" anahtarı geçerlidir (diğerleri bu modüle uygulanmaz)
    const activityForm = (
      <form
        key={`activities:${data.tenant.metadataVersion}`}
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          const fd = new FormData(e.currentTarget);
          saveSettings.mutate({ ...mod.settings, publishToFeed: fd.get('publishToFeed') === 'on' });
        }}
        data-testid="module-settings-form"
      >
        <fieldset className="space-y-3" disabled={!canManage}>
          <legend className="sr-only">{MODULE_LABEL[module]}</legend>
          <Checkbox
            name="publishToFeed"
            defaultChecked={mod.settings.publishToFeed}
            label={tr.settings.publishToFeed}
          />
        </fieldset>
        <Button
          type="submit"
          variant="primary"
          disabled={!canManage}
          loading={saveSettings.isPending}
          data-testid="save-settings"
        >
          {tr.settings.save}
        </Button>
      </form>
    );
    body =
      module === 'activities' ? (
        <div data-testid="settings-page">
          <Suspense fallback={<Skeleton className="h-32" />}>
            <ActivitySettings
              canManage={canManage}
              moduleSettings={activityForm}
              fields={fieldsTab()}
            />
          </Suspense>
        </div>
      ) : module === 'tickets' ? (
        <div data-testid="settings-page">
          <Suspense fallback={<Skeleton className="h-32" />}>
            <TicketSettings
              canManage={canManage}
              moduleSettings={moduleForm}
              renderFields={() => fieldsTab()}
            />
          </Suspense>
        </div>
      ) : module === 'products' ? (
        <div data-testid="settings-page">
          <Suspense fallback={<Skeleton className="h-32" />}>
            <ProductSettings
              canManage={canManage}
              fields={mod.fields}
              initialTab={initialProductTab}
              moduleSettings={moduleForm}
              renderFields={fieldsTab}
            />
          </Suspense>
        </div>
      ) : (
        <div data-testid="settings-page">
          {!canManage ? <Alert tone="info" title={tr.settings.readOnly} className="mb-4" /> : null}
          <Tabs
            className={cn(cardSurface, 'min-w-0 px-4 pb-5 sm:px-5')}
            label={tr.settings.tabs}
            value={tab}
            onChange={(t) => {
              setTab(t as Tab);
            }}
            items={[
              {
                id: 'settings',
                label: tr.settings.tabSettings,
                panel: moduleForm,
              },
              {
                id: 'fields',
                label: tr.settings.tabFields,
                panel: fieldsTab(),
              },
              {
                id: 'feed',
                label: tr.settings.tabFeed,
                panel: (
                  <DescriptionList
                    items={[
                      {
                        // M3 WP 3.10: not etiketleri ayrı ekranda yönetilir (sistem + özel; eski metin listesi
                        // migration 0052 ile oraya taşındı)
                        term: tr.settings.noteTags,
                        value: (
                          <Link
                            to="/settings/feed"
                            className="text-primary underline-offset-2 hover:underline"
                            data-testid="settings-note-tags-link"
                          >
                            {tr.settings.manageNoteTags}
                          </Link>
                        ),
                      },
                      {
                        term: tr.settings.publishState,
                        value: feed?.publish ? tr.settings.on : tr.settings.off,
                      },
                      {
                        term: tr.settings.feedVisibility,
                        value: feed?.showInFeed ? tr.settings.visible : tr.settings.hidden,
                      },
                    ]}
                  />
                ),
              },
              ...(LOOKUP_MODULES.includes(module)
                ? [
                    {
                      id: 'lookups',
                      label: tr.settings.tabLookups,
                      panel: (
                        <Suspense fallback={<Skeleton className="h-32" />}>
                          <LookupSettings />
                        </Suspense>
                      ),
                    },
                  ]
                : []),
            ]}
          />
        </div>
      );
  }

  return (
    <SettingsLayout
      header={header}
      navLabel={tr.settings.modulesNav}
      sections={MODULES.map((m) => ({ id: m, label: MODULE_LABEL[m] }))}
      current={module}
      onSelect={(id) => {
        setModule(id as Module);
      }}
    >
      {body}
    </SettingsLayout>
  );
}

function FieldsTab({
  fields,
  noRequire,
  disabled,
  busy,
  onSave,
  onReset,
}: {
  fields: ModuleMeta['fields'];
  /** Zorunlu yapılamayan alanlar (WP 2.10: ürün `noRequire` — ör. kod/resim; sunucu da reddeder) */
  noRequire?: ReadonlySet<string> | undefined;
  disabled: boolean;
  busy: boolean;
  onSave: (fields: BuiltinFieldSetting[]) => void;
  onReset: () => void;
}) {
  const [state, setState] = useState(fields);
  function toggle(key: string, prop: 'visible' | 'required') {
    setState((s) =>
      s.map((f) => {
        if (f.key !== key || f.locked) return f;
        if (prop === 'required' && noRequire?.has(key) && !f.required) return f;
        const next = { ...f, [prop]: !f[prop] };
        if (prop === 'visible' && !next.visible) next.required = false;
        if (prop === 'required' && next.required) next.visible = true;
        return next;
      }),
    );
  }
  if (state.length === 0)
    return (
      <EmptyState
        headingLevel={3}
        title={tr.settings.fieldsEmptyTitle}
        description={tr.settings.fieldsEmptyBody}
      />
    );
  return (
    <div className="space-y-3" data-testid="fields-tab">
      <div className="relative overflow-x-auto rounded-md border border-border bg-surface">
        <table className="w-full text-sm">
          <caption className="sr-only">{tr.settings.fieldsTable}</caption>
          <thead className="bg-surface-subtle">
            <tr className="border-b border-border text-left text-fg-muted">
              <th scope="col" className="h-row px-cell font-medium">
                {tr.settings.fieldColumn}
              </th>
              <th scope="col" className="h-row w-24 px-cell text-center font-medium">
                {tr.settings.visibleColumn}
              </th>
              <th scope="col" className="h-row w-24 px-cell text-center font-medium">
                {tr.settings.requiredColumn}
              </th>
            </tr>
          </thead>
          <tbody>
            {state.map((f) => (
              <tr key={f.key} className="border-b border-border last:border-b-0">
                <th scope="row" className="h-row px-cell text-left font-normal text-fg">
                  <span className="inline-flex items-center gap-2">
                    {f.label}
                    {f.locked ? <Badge>{tr.settings.lockedField}</Badge> : null}
                  </span>
                </th>
                <td className="h-row px-cell text-center">
                  <Checkbox
                    checked={f.visible}
                    disabled={f.locked || disabled}
                    aria-label={tr.settings.visibleFor(f.label)}
                    onChange={() => {
                      toggle(f.key, 'visible');
                    }}
                    data-testid={`field-visible-${f.key}`}
                  />
                </td>
                <td className="h-row px-cell text-center">
                  <Checkbox
                    checked={f.required}
                    disabled={
                      f.locked || disabled || (noRequire?.has(f.key) === true && !f.required)
                    }
                    aria-label={tr.settings.requiredFor(f.label)}
                    onChange={() => {
                      toggle(f.key, 'required');
                    }}
                    data-testid={`field-required-${f.key}`}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          variant="primary"
          disabled={disabled}
          loading={busy}
          onClick={() => {
            onSave(state);
          }}
          data-testid="save-fields"
        >
          {tr.settings.save}
        </Button>
        <Button variant="ghost" disabled={disabled} onClick={onReset} data-testid="reset-fields">
          {tr.settings.resetFields}
        </Button>
      </div>
    </div>
  );
}
